/* eslint-disable max-statements */

import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import { LoaderFunctionArgs, useFetcher, useLoaderData, useParams } from "react-router-dom";
import { Shifts, getDecodedShift } from "../../utils/decodeShift";
import { doFetch } from "../../utils/doFetch";
import { groupCandidatesByBoard } from "../../utils/groupCandidatesByBoard";
import { SortType } from "../doa-forecast-stats/DoaForecastStatsModels";
import { CandidateInfo, IDispatchAvailabilityListFilter, IDispatchAvailabilityListReportResultsData } from "./DispatchAvailabilityListReportResultasData";
import { DispatchAvailabilityListReportResults } from "./DispatchAvailabilityListReportResults";
import { BoardType, CheckboxFilters, RowsPerPage, Shift } from "./DispatchAvailabilityListReportResultsModels";

export const dispatchAvailabilityListReportResultsRouteLoader = async ({ request, params }: LoaderFunctionArgs) => {
  const { date, shift, job, boardType } = params;
  if (date && shift && job) {
    return doFetch(`audit-report/dispatch-availability/${date}/${shift}/${job}/${boardType ?? BoardType.union}`, { signal: request.signal }, true, true)
  }
  return null;
}

const defaultFilter = {
  selectedBoardType: BoardType.union,
  selectedBoardFilter: '',
  selectedSortType: SortType.buttonPosition,
  selectedNameFilter: '',
  usedDOAnotDispatched: false,
  noDOAnotDispatched: false,
  hasJobOnCurrentShift: false,
  hasJobOnPreviousShift: false,
};

export const DispatchAvailabilityListReportResultsRoute = () => {
  const fetcher = useFetcher();
  const loaderData = useLoaderData() as IDispatchAvailabilityListReportResultsData;
  const data = fetcher.data ?? loaderData;
  const { job, date, shift } = useParams();
  const [filter, setFilter] = useState<IDispatchAvailabilityListFilter>(defaultFilter);
  const [filteredData, setFilteredData] = useState<IDispatchAvailabilityListReportResultsData>(data);
  const [loading, setLoading] = useState(false);
  const [jobDetailsInfo, setJobDetailsInfo] = useState<{ boardCode: string, candidateInfoIndex: number }[]>([])
  const [regId, setRegId] = useState(0);
  const [board, setBoard] = useState('');
  const [selectedCheckboxesFilters, setSelectedCheckboxesFilters] = useState<number[]>([]);
  const [page, setPage] = useState<number>(1);
  const startIndex = (page - 1) * RowsPerPage;
  const endIndex = startIndex + RowsPerPage;
  const boardHasCandidates = filteredData.boardData.find(b => !!b.candidateInfo);

  const rows = boardHasCandidates ? filteredData.boardData?.map(b =>
    b.candidateInfo?.length).reduce((sum = 0, c = 0) => sum + c, 0) : 0;

  const paginatedCandidates = filteredData.boardData
    .flatMap(board => board.candidateInfo)
    .map(c => c?.registrationNumber)
    .slice(startIndex, endIndex);

  const totalPages = rows ? Math.ceil(rows / RowsPerPage) : 0;

  const paginatedData = {
    ...filteredData,
    boardData: filteredData.boardData.map(board => {
      const candidateInfo = board.candidateInfo ?? [];
      return {
        ...board,
        candidateInfo: candidateInfo.filter(
          candidate => paginatedCandidates?.some(regId => candidate.registrationNumber === regId)),
      }
    }).filter(board => board.candidateInfo.length > 0),
  }

  const filterAndSortList = useCallback((
    data: IDispatchAvailabilityListReportResultsData
  ) => {
    let filteredData = { ...data };

    const checkboxesChecked = filter.usedDOAnotDispatched || filter.noDOAnotDispatched ||
      filter.hasJobOnCurrentShift || filter.hasJobOnPreviousShift;

    // Apply all filters first
    if (filter.selectedBoardFilter) {
      filteredData.candidateInfo = filteredData.candidateInfo.filter(d => d.boardCode === filter.selectedBoardFilter);
    }
    if (filter.selectedNameFilter) {
      const filterByName = (c: CandidateInfo) =>
        c.employeeLastName?.includes(filter.selectedNameFilter.toUpperCase());

      filteredData.candidateInfo = filteredData.candidateInfo.filter(c => filterByName(c));
    }
    if (checkboxesChecked) {
      const filterByCheckbox = (canInfo: CandidateInfo) =>
        selectedCheckboxesFilters.length === 0 || selectedCheckboxesFilters.includes(canInfo.filterStatus);

      filteredData.candidateInfo = filteredData.candidateInfo.filter(filterByCheckbox);
    }    // Then apply sorting
    if (filter.selectedSortType === SortType.buttonPosition) {
      filteredData.candidateInfo.sort((a, b) => a.sortButtonPosition - b.sortButtonPosition);
    } else if (filter.selectedSortType === SortType.boardPlate) {
      filteredData.candidateInfo.sort((a, b) => a.sortCandidatePosition - b.sortCandidatePosition);
    } else if (filter.selectedSortType === SortType.name) {
      filteredData.candidateInfo.sort((a, b) => (a.employeeLastName || '').localeCompare(b.employeeLastName || ''));
    }    // Finally, regroup candidates
    if (filter.selectedSortType === SortType.name) {
      const signleGroup = {
        sortBoardPosition: 0,
        boardCode: "",
        boardDescription: "",
        isCasual: false,
        buttonImage: null,
        buttonDescription: null,
        plateNumber: null,
        candidateInfo: filteredData.candidateInfo,
        buttonInfo: []
      }

      filteredData.boardData = [signleGroup]
    } else {
      filteredData.boardData = groupCandidatesByBoard(filteredData.candidateInfo, data.boardData);
    }
    return filteredData;
  }, [filter, selectedCheckboxesFilters]);

  useEffect(() => {
    setLoading(fetcher.state === 'loading');
  }, [fetcher.state])

  useEffect(() => {
    if (data) {
      setFilteredData(filterAndSortList(data));
    }
  }, [data, filterAndSortList])

  const onLoadData = (boardType = filter.selectedBoardType) =>
    fetcher.load(`/dispatch-availability-list-report-results/${date}/${shift}/${job}/${boardType}`);

  const updateCheckboxFilters = (
    updatedFilter: IDispatchAvailabilityListFilter,
    checkboxesFilters: { key: number; filter: keyof IDispatchAvailabilityListFilter }[]
  ) => {
    const updatedFilters = checkboxesFilters.filter(({ filter }) => updatedFilter[filter]);
    setSelectedCheckboxesFilters(updatedFilters.map(({ key }) => key));
  };

  const updateFilterState = (updatedFilter: IDispatchAvailabilityListFilter) => {
    setFilter((prev) =>
      prev.selectedBoardType !== updatedFilter.selectedBoardType
        ? { ...defaultFilter, selectedBoardType: updatedFilter.selectedBoardType }
        : updatedFilter
    );
  };

  const loadDataIfNeeded = (updatedFilter: IDispatchAvailabilityListFilter) => {
    if (filter.selectedBoardType !== updatedFilter.selectedBoardType) {
      onLoadData(updatedFilter.selectedBoardType);
      updateFilterState(updatedFilter);
      setPage(1);
    }
  };

  const onFilterUpdate = (updatedFilter: IDispatchAvailabilityListFilter) => {
    updateCheckboxFilters(updatedFilter, CheckboxFilters);
    updateFilterState(updatedFilter);
    loadDataIfNeeded(updatedFilter);
    setPage(1);
  };

  const onPageChange = (_event: React.ChangeEvent<unknown>, page: number) => setPage(page);

  const onSetOpenJobDetails = (boardCode: string, candidateInfoIndex: number) =>
    setJobDetailsInfo(ji => {
      const jobInfo = ji.find(i => i.boardCode === boardCode && i.candidateInfoIndex === candidateInfoIndex);
      return jobInfo ? ji.filter(i => i.boardCode !== boardCode || i.candidateInfoIndex !== candidateInfoIndex) :
        [...ji, { boardCode, candidateInfoIndex }]
    })

  const onOpenClosePlateModal = (regId = 0, board = '') => {
    setRegId(regId);
    setBoard(board);
  }

  const onRefresh = () => {
    onLoadData(); // Call onLoadData to refresh data
    setPage(1); // Reset page to 1
    setSelectedCheckboxesFilters([]); // Reset selectedCheckboxesFilters to empty array

    // Reset filter to default values except selected board type
    setFilter({
      ...defaultFilter,
      selectedBoardType: filter.selectedBoardType
    });
  };

  return (
    <DispatchAvailabilityListReportResults
      data={paginatedData}
      onFilterUpdate={onFilterUpdate}
      filter={filter}
      loading={loading}
      onRefresh={onRefresh}
      page={page}
      totalPages={totalPages}
      onPageChange={onPageChange}
      onSetOpenJobDetails={onSetOpenJobDetails}
      jobDetailsInfo={jobDetailsInfo}
      onOpenClosePlateModal={onOpenClosePlateModal}
      board={board}
      regId={regId}
      date={dayjs(date, "DDMMYYYY").format('DD/MM/YYYY')}
      shift={getDecodedShift(Shifts.find(s => s === shift) as Shift)}
    />
  )
}